import { Box, Flex, Group, useMantineTheme } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangeSelects, type ISelectOption, Select } from '@feathr/components';

import type { TDateRange, TPreset } from '../DonationFormReport.utils';

export interface IPresetOption<T extends string = TPreset> extends ISelectOption {
  id: T;
}

export interface ITimeframeControlsProps<T extends string = TPreset> {
  /** The selected preset. */
  preset: T;
  /** The resolved date window as `[startISO, endISO]`. */
  range: TDateRange;
  /**
   * Selectable preset options. When omitted, defaults to the four donation-form presets
   * (7d, 30d, 90d, custom).
   */
  options?: Array<IPresetOption<T>>;
  /** Called when the preset changes. Callers should re-derive the range for non-custom presets. */
  onPresetChange: (preset: T) => void;
  /** Called when either date picker changes. Only fires in custom mode. */
  onRangeChange: (range: TDateRange) => void;
}

function buildDefaultOptions(t: TFunction): Array<IPresetOption<TPreset>> {
  return [
    { id: '7d', name: t('Last 7 days') },
    { id: '30d', name: t('Last 30 days') },
    { id: '90d', name: t('Last 90 days') },
    { id: 'custom', name: t('Custom') },
  ];
}

function TimeframeControls<T extends string = TPreset>({
  preset,
  range,
  options: optionsProp,
  onPresetChange,
  onRangeChange,
}: Readonly<ITimeframeControlsProps<T>>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const defaultOptions = useMemo(() => buildDefaultOptions(t), [t]);
  const options = (optionsProp ?? defaultOptions) as Array<IPresetOption<T>>;
  const selected = options.find(({ id }) => id === preset);
  const isCustom = preset === 'custom';

  function handleSelect(option: IPresetOption<T>): void {
    onPresetChange(option.id);
  }

  function handleRangeChange(value: [string | undefined, string | undefined]): void {
    onRangeChange([value[0] ?? range[0], value[1] ?? range[1]]);
  }

  return (
    <Flex
      align={'flex-end'}
      data-name={'timeframe-controls'}
      gap={theme.spacing['4']}
      justify={'space-between'}
      wrap={'wrap'}
    >
      <Box w={200}>
        <Select<IPresetOption<T>>
          getOptionLabel={({ name }): string => name}
          getOptionValue={({ id }): string => id}
          label={t('Timeframe')}
          name={'timeframe-preset'}
          onSelectSingle={handleSelect}
          options={options}
          value={selected}
        />
      </Box>
      {isCustom && (
        <Group gap={theme.spacing['2']}>
          <DateRangeSelects onChange={handleRangeChange} t={t} value={[range[0], range[1]]} />
        </Group>
      )}
    </Flex>
  );
}

export default TimeframeControls;
